import { dashboardObservability } from "../utils/observability";
import { defineEvent } from "@diverge/observability/browser";
import { useCallback, useRef, useState } from "react";
import api from "../utils/api";
import { reportRequestFailure } from "../utils/requestFailure";
import type { ManageableOrganizationsSummary } from "../types";
import {
  getRequestConfig,
  type OrganizationAccessStatus,
  type RefreshManageableOrganizationsOptions,
} from "./authContextSupport";

const operationalLogger = dashboardObservability.logger("dashboard/context/useOrganizationAccess");
const organizationAccessFailed = defineEvent({
  name: "dashboard.auth.organization_access_failed",
  message: "Organization access check failed during sign-in",
  level: "error",
  attributes: {
    "di.reason_code": { type: "category", values: ["organization_capability_check_failed"] },
  },
  classification: "operational",
  volume: "boundary",
  owner: "dashboard/src/context/useOrganizationAccess",
});

let resolvedOrganizationAccessStatus: OrganizationAccessStatus = "unknown";
const EMPTY_SUMMARY: ManageableOrganizationsSummary = {
  items: [],
  page_number: 0,
  page_size: 1,
  total_count: 0,
};

/** Manages the organization capability gate used by authentication. */
export function useOrganizationAccess() {
  const [manageableOrganizationsCount, setManageableOrganizationsCount] = useState(0);
  const [organizationAccessStatus, setOrganizationAccessStatus] = useState(
    resolvedOrganizationAccessStatus,
  );
  const organizationAccessStatusRef = useRef(resolvedOrganizationAccessStatus);

  const syncOrganizationAccessStatus = useCallback((nextStatus: OrganizationAccessStatus) => {
    resolvedOrganizationAccessStatus = nextStatus;
    organizationAccessStatusRef.current = nextStatus;
    setOrganizationAccessStatus(nextStatus);
  }, []);

  const resetOrganizationAccessState = useCallback(() => {
    syncOrganizationAccessStatus("unknown");
    setManageableOrganizationsCount(0);
  }, [syncOrganizationAccessStatus]);

  const markOrganizationAccessDenied = useCallback(() => {
    syncOrganizationAccessStatus("denied");
    setManageableOrganizationsCount(0);
  }, [syncOrganizationAccessStatus]);

  const refreshManageableOrganizations = useCallback(
    async ({
      suppressErrors = false,
      requestTimeoutMs,
      skipAuthRecovery = false,
    }: RefreshManageableOrganizationsOptions = {}): Promise<ManageableOrganizationsSummary> => {
      if (organizationAccessStatusRef.current === "denied") return EMPTY_SUMMARY;
      try {
        const response = await api.get(
          "/api/v2/organizations",
          getRequestConfig(
            { params: { page_number: 0, page_size: 1 }, skipAuthRecovery },
            requestTimeoutMs,
          ),
        );
        const totalCount =
          typeof response?.data?.total_count === "number" &&
          Number.isFinite(response.data.total_count)
            ? response.data.total_count
            : 0;
        syncOrganizationAccessStatus("allowed");
        setManageableOrganizationsCount(totalCount);
        return response.data;
      } catch (error) {
        if ((error as { response?: { status?: number } })?.response?.status === 403) {
          markOrganizationAccessDenied();
          return EMPTY_SUMMARY;
        }
        // Expired sessions, denials and lost connectivity are normal operation,
        // and a 5xx is the API's own failure.
        reportRequestFailure(
          error,
          "organization_capability_check_failed",
          suppressErrors
            ? undefined
            : (failure) =>
                operationalLogger.error(organizationAccessFailed, failure, {
                  "di.reason_code": "organization_capability_check_failed",
                }),
        );
        if (suppressErrors) return EMPTY_SUMMARY;
        throw error;
      }
    },
    [markOrganizationAccessDenied, syncOrganizationAccessStatus],
  );

  return {
    manageableOrganizationsCount,
    organizationAccessStatus,
    organizationAccessStatusRef,
    resetOrganizationAccessState,
    markOrganizationAccessDenied,
    refreshManageableOrganizations,
  };
}
